import { useState, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  fetchChecklistById,
  replaceInstructionCompetencies,
  updateChecklist,
} from "@/modules/checklist/api";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Textarea } from "@/shared/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/shared/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Switch } from "@/shared/ui/switch";
import { Badge } from "@/shared/ui/badge";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { Separator } from "@/shared/ui/separator";
import { ArrowLeft, Save, Tag, X, Search, Settings2, Plus, Sparkles, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { ChecklistItemEditor } from "./ChecklistItemEditor";
import { CompletionsTab } from "./CompletionsTab";

interface FactoryArea {
  id: string;
  name: string;
  color: string | null;
}

interface ProductionLine {
  id: string;
  name: string;
  area_id: string;
}

interface Station {
  id: string;
  name: string;
  line_id: string;
}

interface Competency {
  id: string;
  name: string;
  description: string | null;
  color: string;
  is_global: boolean;
}

interface Checklist {
  id: string;
  tenant_id: string;
  factory_id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  area_id: string | null;
  line_id: string | null;
  station_id: string | null;
}

interface ChecklistBuilderProps {
  factoryId: string;
  tenantId: string;
  instructionId: string;
  onBack: () => void;
}

export function ChecklistBuilder({ factoryId, tenantId, instructionId, onBack }: ChecklistBuilderProps) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const validTabs = ['items', 'settings', 'completions'] as const;
  type Tab = typeof validTabs[number];
  const initialTab: Tab = validTabs.includes(tabParam as Tab) ? (tabParam as Tab) : 'items';
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'items') {
        next.delete('tab');
      } else {
        next.set('tab', tab);
      }
      return next;
    });
  };

  const [addItemFn, setAddItemFn] = useState<(() => void) | null>(null);
  
  const handleRegisterAddItem = useCallback((fn: () => void) => {
    setAddItemFn(() => fn);
  }, []);
  
  // Settings form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [assignment, setAssignment] = useState<string>("none");
  const [isActive, setIsActive] = useState(true);
  const [selectedCompetencies, setSelectedCompetencies] = useState<Competency[]>([]);
  const [competencySearch, setCompetencySearch] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  const [isCreatingCompetency, setIsCreatingCompetency] = useState(false);
  const [newCompetencyName, setNewCompetencyName] = useState("");
  const [newCompetencyDescription, setNewCompetencyDescription] = useState("");
  const [newCompetencyColor, setNewCompetencyColor] = useState("#0891b2");

  const COMPETENCY_COLORS = [
    "#0891b2", // cyan
    "#16a34a", // green
    "#ca8a04", // yellow
    "#dc2626", // red
    "#9333ea", // purple
    "#2563eb", // blue
    "#ea580c", // orange
    "#64748b", // slate
  ];

  // Fetch the instruction details
  const { data: instruction, isLoading: isLoadingInstruction } = useQuery({
    queryKey: ["instruction-details", tenantId, instructionId],
    queryFn: async () => {
      const data = await fetchChecklistById(tenantId, instructionId);
      if (!data) throw new Error("Work instruction not found");
      return {
        ...data,
        competencies: (data.instruction_competencies || [])
          .map((wic) => wic.competency)
          .filter(Boolean) as Competency[],
      } as Checklist & { competencies: Competency[] };
    },
    enabled: !!tenantId && !!instructionId,
  });

  // Initialize form when instruction loads
  if (instruction && !isInitialized) {
    setName(instruction.name);
    setDescription(instruction.description || "");
    if (instruction.station_id) {
      setAssignment(`station:${instruction.station_id}`);
    } else if (instruction.line_id) {
      setAssignment(`line:${instruction.line_id}`);
    } else if (instruction.area_id) {
      setAssignment(`area:${instruction.area_id}`);
    } else {
      setAssignment("none");
    }
    setIsActive(instruction.is_active);
    setSelectedCompetencies(instruction.competencies || []);
    setIsInitialized(true);
  }

  // Fetch all available competencies
  const { data: allCompetencies = [] } = useQuery({
    queryKey: ["competencies", factoryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('core')
        .from("competencies")
        .select("id, name, description, color, is_global")
        .or(`is_global.eq.true,factory_id.eq.${factoryId}`)
        .order("name");
      if (error) throw error;
      return data as Competency[];
    },
    enabled: !!factoryId,
  });

  // Filter competencies based on search
  const filteredCompetencies = useMemo(() => {
    if (!competencySearch.trim()) return allCompetencies;
    const search = competencySearch.toLowerCase();
    return allCompetencies.filter(c => 
      c.name.toLowerCase().includes(search) || 
      c.description?.toLowerCase().includes(search)
    );
  }, [allCompetencies, competencySearch]);

  // Fetch factory structure
  const { data: factoryAreas = [] } = useQuery({
    queryKey: ["factory-areas", factoryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('core')
        .from("factory_areas")
        .select("id, name, color")
        .eq("factory_id", factoryId)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as FactoryArea[];
    },
  });

  const { data: productionLines = [] } = useQuery({
    queryKey: ["production-lines", factoryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('core')
        .from("production_lines")
        .select("id, name, area_id")
        .eq("factory_id", factoryId)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as ProductionLine[];
    },
  });

  const { data: stations = [] } = useQuery({
    queryKey: ["stations", factoryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('core')
        .from("stations")
        .select("id, name, line_id")
        .eq("factory_id", factoryId)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as Station[];
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      const { area_id, line_id, station_id } = parseAssignment();

      await updateChecklist(tenantId, instructionId, {
          name,
          description: description || null,
          area_id,
          line_id,
          station_id,
          is_active: isActive,
      });

      await replaceInstructionCompetencies(
        tenantId,
        instructionId,
        selectedCompetencies.map((competency) => competency.id),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["instruction-details", tenantId, instructionId] });
      toast.success("Work instruction saved");
    },
    onError: (error) => toast.error("Failed to save: " + error.message),
  });

  const createCompetencyMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase
        .schema('core')
        .from("competencies")
        .insert({
          name: newCompetencyName.trim(),
          description: newCompetencyDescription.trim() || null,
          color: newCompetencyColor,
          factory_id: factoryId,
          tenant_id: tenantId,
          is_global: false,
        })
        .select()
        .single();
      if (error) throw error;
      return data as Competency;
    },
    onSuccess: (newCompetency) => {
      queryClient.invalidateQueries({ queryKey: ["competencies", factoryId] });
      setSelectedCompetencies(prev => [...prev, newCompetency]);
      setNewCompetencyName("");
      setNewCompetencyDescription("");
      setNewCompetencyColor("#0891b2");
      setIsCreatingCompetency(false);
      setCompetencySearch("");
      toast.success(`"${newCompetency.name}" created and added`);
    },
    onError: (error) => toast.error("Failed to create competency: " + error.message),
  });

  const parseAssignment = () => {
    if (assignment === "none") return { area_id: null, line_id: null, station_id: null };
    const [type, id] = assignment.split(":");
    return {
      area_id: type === "area" ? id : null,
      line_id: type === "line" ? id : null,
      station_id: type === "station" ? id : null,
    };
  };

  const toggleCompetency = (competency: Competency) => {
    setSelectedCompetencies(prev => {
      const exists = prev.find(c => c.id === competency.id);
      if (exists) {
        return prev.filter(c => c.id !== competency.id);
      }
      return [...prev, competency];
    });
  };

  if (isLoadingInstruction || !instruction) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-foreground">{name || "Work Instruction"}</h1>
            <p className="text-sm text-muted-foreground">Edit instruction settings and items</p>
          </div>
        </div>
        {activeTab === 'settings' ? (
          <Button 
            variant="industrial" 
            className="gap-2"
            onClick={() => updateMutation.mutate()}
            disabled={updateMutation.isPending}
          >
            <Save className="w-4 h-4" />
            Save Changes
          </Button>
        ) : activeTab === 'items' ? (
          <Button 
            className="gap-2"
            onClick={() => addItemFn?.()}
          >
            <Plus className="w-4 h-4" />
            Add Item
          </Button>
        ) : null}
      </div>

      {/* Tab Switcher */}
      <div className="flex items-center gap-1 p-1 bg-muted rounded-lg w-fit">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleTabChange('items')}
          className={activeTab === 'items' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}
        >
          Items & Steps
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={`gap-2 ${activeTab === 'settings' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`}
          onClick={() => handleTabChange('settings')}
        >
          <Settings2 className="w-4 h-4" />
          Settings
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={`gap-2 ${activeTab === 'completions' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`}
          onClick={() => handleTabChange('completions')}
        >
          <ClipboardList className="w-4 h-4" />
          Completions
        </Button>
      </div>

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Basic Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Basic Information</CardTitle>
              <CardDescription>Name, description, and assignment</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Morning Shift Startup"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional description"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label>Assign To</Label>
                <Select value={assignment} onValueChange={setAssignment}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectItem value="none">
                      <span className="font-medium">Factory-wide</span>
                    </SelectItem>
                    {factoryAreas.map((area) => (
                      <div key={area.id}>
                        <SelectItem value={`area:${area.id}`}>
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: area.color || "var(--primary)" }} />
                            <span className="font-medium">{area.name}</span>
                          </div>
                        </SelectItem>
                        {productionLines
                          .filter(line => line.area_id === area.id)
                          .map((line) => (
                            <div key={line.id}>
                              <SelectItem value={`line:${line.id}`}>
                                <span className="pl-4 text-muted-foreground">└ {line.name}</span>
                              </SelectItem>
                              {stations
                                .filter(station => station.line_id === line.id)
                                .map((station) => (
                                  <SelectItem key={station.id} value={`station:${station.id}`}>
                                    <span className="pl-8 text-muted-foreground">└ {station.name}</span>
                                  </SelectItem>
                                ))}
                            </div>
                          ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between pt-2">
                <div>
                  <Label htmlFor="active">Active</Label>
                  <p className="text-xs text-muted-foreground">Make this instruction available to operators</p>
                </div>
                <Switch id="active" checked={isActive} onCheckedChange={setIsActive} />
              </div>
            </CardContent>
          </Card>

          {/* Competencies */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Tag className="w-5 h-5 text-primary" />
                <CardTitle className="text-lg">Required Competencies</CardTitle>
              </div>
              <CardDescription>
                Select the skills and certifications required to perform this work instruction
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Selected competencies */}
              {selectedCompetencies.length > 0 && (
                <div className="flex flex-wrap gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
                  {selectedCompetencies.map((comp) => (
                    <Badge
                      key={comp.id}
                      variant="secondary"
                      className="gap-1 pr-1 cursor-pointer hover:bg-destructive/20 transition-colors"
                      style={{ borderColor: comp.color, borderWidth: 1 }}
                      onClick={() => toggleCompetency(comp)}
                    >
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: comp.color }} />
                      {comp.name}
                      <X className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              )}

              {selectedCompetencies.length === 0 && (
                <div className="p-3 rounded-lg bg-muted/50 border border-dashed border-border text-center text-sm text-muted-foreground">
                  No competencies selected. Select from the list below.
                </div>
              )}

              <Separator />

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search competencies..."
                  value={competencySearch}
                  onChange={(e) => setCompetencySearch(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* Available competencies or create new */}
              <ScrollArea className="h-[300px] rounded-lg border border-border">
                <div className="p-2 space-y-1">
                  {isCreatingCompetency ? (
                    <div className="p-3 space-y-3 bg-muted/30 rounded-lg border border-dashed border-primary/30">
                      <div className="flex items-center gap-2 text-sm font-medium text-primary">
                        <Sparkles className="w-4 h-4" />
                        Create New Competency
                      </div>
                      <Input
                        placeholder="Competency name *"
                        value={newCompetencyName}
                        onChange={(e) => setNewCompetencyName(e.target.value)}
                        autoFocus
                      />
                      <Input
                        placeholder="Description (optional)"
                        value={newCompetencyDescription}
                        onChange={(e) => setNewCompetencyDescription(e.target.value)}
                      />
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Color</Label>
                        <div className="flex gap-2 flex-wrap">
                          {COMPETENCY_COLORS.map((color) => (
                            <button
                              key={color}
                              type="button"
                              onClick={() => setNewCompetencyColor(color)}
                              className={`w-6 h-6 rounded-full transition-all ${
                                newCompetencyColor === color 
                                  ? 'ring-2 ring-offset-2 ring-offset-background ring-primary scale-110' 
                                  : 'hover:scale-105'
                              }`}
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-2 pt-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => {
                            setIsCreatingCompetency(false);
                            setNewCompetencyName("");
                            setNewCompetencyDescription("");
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 gap-1"
                          onClick={() => createCompetencyMutation.mutate()}
                          disabled={!newCompetencyName.trim() || createCompetencyMutation.isPending}
                        >
                          <Plus className="w-3 h-3" />
                          Create
                        </Button>
                      </div>
                    </div>
                  ) : filteredCompetencies.length === 0 && competencySearch.trim() ? (
                    <div className="py-6 px-4 text-center space-y-3">
                      <p className="text-sm text-muted-foreground">
                        No competencies match "{competencySearch}"
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={() => {
                          setNewCompetencyName(competencySearch.trim());
                          setIsCreatingCompetency(true);
                        }}
                      >
                        <Plus className="w-4 h-4" />
                        Create "{competencySearch.trim()}"
                      </Button>
                    </div>
                  ) : filteredCompetencies.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No competencies available
                    </p>
                  ) : (
                    filteredCompetencies.map((comp) => {
                      const isSelected = selectedCompetencies.some(c => c.id === comp.id);
                      return (
                        <button
                          key={comp.id}
                          type="button"
                          onClick={() => toggleCompetency(comp)}
                          className={`w-full text-left px-3 py-2.5 rounded-md transition-all flex items-center gap-3 ${
                            isSelected 
                              ? 'bg-primary/10 border-2 border-primary/40 shadow-sm' 
                              : 'hover:bg-muted border-2 border-transparent'
                          }`}
                        >
                          <span 
                            className={`w-4 h-4 rounded-full flex-shrink-0 ${isSelected ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''}`}
                            style={{ backgroundColor: comp.color }} 
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">{comp.name}</p>
                            {comp.description && (
                              <p className="text-xs text-muted-foreground line-clamp-1">{comp.description}</p>
                            )}
                          </div>
                          {isSelected && (
                            <Badge variant="default" className="text-xs flex-shrink-0">Selected</Badge>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Items Tab */}
      {activeTab === 'items' && instruction && (
        <ChecklistItemEditor
          checklist={{
            id: instruction.id,
            tenant_id: instruction.tenant_id,
            name: instruction.name,
            description: instruction.description,
            factory_id: instruction.factory_id,
            area_id: instruction.area_id,
            line_id: instruction.line_id,
            station_id: instruction.station_id,
          }}
          onBack={onBack}
          embedded
          onRegisterAddItem={handleRegisterAddItem}
        />
      )}

      {/* Completions Tab */}
      {activeTab === 'completions' && (
        <div className="bg-card rounded-xl border border-border p-6">
          <CompletionsTab
            tenantId={tenantId}
            factoryId={factoryId}
            templateId={instructionId}
          />
        </div>
      )}
    </div>
  );
}
